import { CommonModule, NgClass } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    Signal,
    SimpleChanges,
    ViewChild,
    ViewContainerRef,
    computed,
    effect,
    input,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, EventType, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ExportChannel } from 'exports/interfaces/export-channels.interface';
import { ListingChannelComparisonService } from 'listings/services/listing-channel-comparison.service';
import { debounce, isEqual } from 'lodash-es';
import isFunction from 'lodash-es/isFunction';
import { BehaviorSubject, filter, map, shareReplay, startWith, switchMap, take } from 'rxjs';
import { ChannelsService } from 'settings/services/channels.service';
import { TabHeaderComponent } from 'shared/components/tab-outline/tab-header/tab-header.component';
import { RESTRICTIVE_UI_LISTINGS } from 'shared/constants/storage-keys';
import { PermissionDirective } from 'shared/directives/permission.directive';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { EventBusService, StorageService } from 'shared/services';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { Tab, TabKeyEnum } from '../../interfaces/tab.interface';

@Component({
    selector: 'app-tab-outline',
    templateUrl: './tab-outline.component.html',
    styleUrls: ['./tab-outline.component.scss'],
    imports: [NgClass, TranslateModule, TabHeaderComponent, PermissionDirective, CommonModule],
})
export class TabOutlineComponent implements AfterViewInit, OnChanges {
    @ViewChild('tabContentContainer', { read: ViewContainerRef }) tabContentContainer!: ViewContainerRef;
    @ViewChild('tabContentDisclaimer', { read: ViewContainerRef }) tabContentDisclaimer!: ViewContainerRef;
    @ViewChild('tabHeader', { read: ElementRef }) tabHeader!: ElementRef;

    @Output() tabChange = new EventEmitter<string>();
    @Output() selectedTabIndexChange = new EventEmitter<number>();

    @Input() data: unknown;
    @Input() tabs: Tab[] = [];
    @Input() selectedTabIndex: number = 0;
    hideTabsNavigation = input<boolean>(false);

    private pendingTabIndex: number | null = null;
    private lastSuccessfulTabIndex: number = 0;

    private tabsSubject = new BehaviorSubject<Tab[]>(this.tabs);
    private readonly tabs$ = this.tabsSubject.asObservable();

    scrollActiveTabIntoViewDebounced = debounce(() => {
        this.scrollActiveTabIntoView();
    }, 100);

    showRestrictiveUiInfoNotification = toSignal(
        this.eventBus.getObservableForEventStream('listing:userPreferences:restrictiveUiChannelsUpdate').pipe(
            startWith(null),
            map(() => this.getRestrictiveUIPreference()),
        ),
        { initialValue: this.getRestrictiveUIPreference() },
    );

    private getRestrictiveUIPreference(): boolean {
        const listingId = this.activatedRoute.snapshot.params['id'];
        const currentUrl = this.router.url;
        const storedPreferences = JSON.parse((this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS) as string) || '{}');
        return (
            !!storedPreferences[listingId] &&
            this.featureFlagService.featureFlags()[FeatureFlagEnum.RESTRICTIVE_UI] &&
            !currentUrl.includes('details/visuals')
        );
    }

    private readonly channelsForFullExport$ = this.tabs$.pipe(
        filter((tabs) => {
            const isContentAndVisualsWrapperTab = tabs?.some((tab) => tab.key === TabKeyEnum.CONTENT_AREA);
            return Array.isArray(tabs) && tabs.length > 0 && !isContentAndVisualsWrapperTab;
        }),
        take(1),
        switchMap(() => this.channelsService.getChannelsForFullExport()),
        shareReplay(1),
    );

    public allChannelsForExport: Signal<ExportChannel[]> = toSignal(this.channelsForFullExport$, { initialValue: [] });

    public restrictiveUiChannel = computed<ExportChannel | undefined>(() => {
        const listingId = this.activatedRoute.snapshot.params['id'];
        const storedRestriveUiListings = this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS) as string | null;

        if (!storedRestriveUiListings) return;

        const restrictiveUiForCurentListing = JSON.parse(storedRestriveUiListings)[listingId];

        if (!restrictiveUiForCurentListing) return;

        return this.allChannelsForExport().find((channel) => channel.value === restrictiveUiForCurentListing);
    });

    private currentSelectedTabComponentReference: any;
    private hasViewInitialized = false;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private eventBus: EventBusService,
        private destroyRef: DestroyRef,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private storageService: StorageService,
        private featureFlagService: FeatureFlagService,
        private channelsService: ChannelsService,
        private listingChannelComparisonService: ListingChannelComparisonService,
    ) {
        effect(() => {
            if (this.featureFlagService.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON]) {
                this.listingChannelComparisonService.comparisonSourceModifiedFeatureGroups();
                this.listingChannelComparisonService.tabFieldName();
                this.applySnapshotTabHighlights();
            }
        });
        this.eventBus
            .getObservableForEventStream('listing:compare')
            .pipe(
                // In one of the previous itterations, the debounceTime was needed in order to avoid "old" events messing up the tab highlights.
                // At this time it doesn't appear to be necessary anymore, as the issue was fixed at the root cause (unintentionally),
                // but keeping the line commented out for now, just in case the issue reappears to have a starting point for the fix.
                // debounceTime(51),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((eventData: any) => {
                this.tabs = this.tabs.map((tab) => {
                    const matchingFeatureGroup = eventData?.changedFeatureGroups?.findIndex(
                        (featureGroup: any) => featureGroup.label === tab.data?.fieldName,
                    );
                    tab.highlighted = matchingFeatureGroup !== undefined && matchingFeatureGroup !== -1;
                    return tab;
                });
            });
        this.router.events
            .pipe(
                filter(
                    (event) =>
                        event instanceof NavigationStart ||
                        event instanceof NavigationEnd ||
                        event instanceof NavigationCancel ||
                        event instanceof NavigationError,
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => {
                switch (event.type) {
                    case EventType.NavigationStart:
                        this.pendingTabIndex = this.selectedTabIndex;
                        break;

                    case EventType.NavigationEnd:
                        this.lastSuccessfulTabIndex = this.selectedTabIndex;
                        this.pendingTabIndex = null;
                        break;

                    case EventType.NavigationCancel:
                    case EventType.NavigationError:
                        if (this.pendingTabIndex !== null) {
                            this.selectedTabIndex = this.lastSuccessfulTabIndex;
                            this.pendingTabIndex = null;
                            this.changeDetectorRef.detectChanges();
                            this.scrollActiveTabIntoViewDebounced();
                        }
                        break;
                }
            });
    }

    public ngAfterViewInit(): void {
        // This ensures the selected tab is rendered on load
        this.selectTab(this.selectedTabIndex, false);
        this.lastSuccessfulTabIndex = this.selectedTabIndex;
        this.changeDetectorRef.detectChanges();
        this.scrollActiveTabIntoViewDebounced();
        this.hasViewInitialized = true;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['selectedTabIndex'] &&
            this.hasViewInitialized &&
            changes['selectedTabIndex'].currentValue !== changes['selectedTabIndex'].previousValue
        ) {
            this.selectTab(this.selectedTabIndex, true);
            this.scrollActiveTabIntoViewDebounced();
        }

        if (changes['tabs'] && this.hasViewInitialized) {
            const areTabsChanged = !isEqual(changes['tabs'].currentValue, changes['tabs'].previousValue);

            if (areTabsChanged) {
                this.selectTab(this.selectedTabIndex, true);
            }
        }

        if (
            changes['tabs'] &&
            changes['tabs'].currentValue &&
            changes['tabs'].previousValue !== changes['tabs'].currentValue &&
            Array.isArray(changes['tabs'].currentValue)
        ) {
            this.tabsSubject.next(changes['tabs'].currentValue);

            if (this.featureFlagService.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON]) {
                this.applySnapshotTabHighlights();
            }
        }
    }

    private applySnapshotTabHighlights(): void {
        const modifiedFeatureGroups = this.listingChannelComparisonService.comparisonSourceModifiedFeatureGroups();

        this.tabs = this.tabs.map((tab) => {
            const matchingFeatureGroup = modifiedFeatureGroups?.find((featureGroup) => featureGroup.label === tab.data?.fieldName);
            tab.highlighted = !!matchingFeatureGroup;
            return tab;
        });
    }

    private selectTab(index: number, noEvent?: boolean): void {
        const tab = this.tabs[index];

        if (!this.tabs.length || !tab || tab.disabled || !this.tabContentContainer) {
            return;
        }

        if (tab.link) {
            if (!this.router.url.includes(tab.link)) {
                this.router.navigateByUrl(tab.link);
            }

            this.selectedTabIndex = index;
            return;
        }

        if (
            this.currentSelectedTabComponentReference?.instance &&
            isFunction(this.currentSelectedTabComponentReference.instance.canDeactivateTab) &&
            !this.currentSelectedTabComponentReference.instance.canDeactivateTab()
        ) {
            return;
        }

        if (!noEvent) {
            this.selectedTabIndexChange.emit(index);
            this.tabChange.emit(tab.label);
            this.changeDetectorRef.detectChanges();
        }

        this.tabContentContainer.clear();

        if (tab.component) {
            this.currentSelectedTabComponentReference = this.tabContentContainer.createComponent(tab.component);
            this.currentSelectedTabComponentReference.instance.data = tab.data;
        }
    }

    public tabSelectHandler(index: number) {
        if (index === this.selectedTabIndex) {
            return;
        }
        this.selectedTabIndex = index;
        this.selectTab(index);
        this.scrollActiveTabIntoViewDebounced();
    }

    private scrollActiveTabIntoView(): void {
        this.tabHeader?.nativeElement?.querySelector('.active')?.scrollIntoView({
            inline: 'center',
            behavior: 'smooth',
            block: 'nearest',
        });
        this.changeDetectorRef.detectChanges();
    }
}
